import jsPDF from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import { supabase } from '@/integrations/supabase/client';

export interface PackagePDFData {
  id: string;
  title: string;
  validUntil: string | null;
  customer: { name: string; phone?: string; email?: string } | null;
  company: { name: string; phone?: string; address?: string };
  items: Array<{ name: string; code: string; quantity: number; price: number; total: number }>;
  subtotal: number;
  discount: number;
  total: number;
  note?: string;
  createdAt: string;
}

// ================= Unicode-safe helpers (fonts) =================
const FONT_URL_REGULAR = '/fonts/NotoSans-Regular.ttf';
const FONT_URL_BOLD = '/fonts/NotoSans-Bold.ttf';
let fontsReady: Promise<boolean> | null = null;
let fontsLoaded = false;

const ensureUnicodeFonts = () => {
  if (!fontsReady) {
    fontsReady = (async () => {
      try {
        console.log('Attempting to load fonts...');
        console.log('Regular font URL:', FONT_URL_REGULAR);
        console.log('Bold font URL:', FONT_URL_BOLD);
        
        // Try to load regular font
        const r = await fetch(FONT_URL_REGULAR);
        console.log('Regular font response:', r.status, r.statusText);
        if (!r.ok) throw new Error(`Failed to load regular font: ${r.status} ${r.statusText}`);
        const rBuf = await r.arrayBuffer();
        console.log('Regular font loaded, size:', rBuf.byteLength);
        const rB64 = btoa(String.fromCharCode(...new Uint8Array(rBuf)));

        // Try to load bold font
        const b = await fetch(FONT_URL_BOLD);
        console.log('Bold font response:', b.status, b.statusText);
        if (!b.ok) throw new Error(`Failed to load bold font: ${b.status} ${b.statusText}`);
        const bBuf = await b.arrayBuffer();
        console.log('Bold font loaded, size:', bBuf.byteLength);
        const bB64 = btoa(String.fromCharCode(...new Uint8Array(bBuf)));

        // Register in jsPDF VFS once globally
        (jsPDF as any).API.events.push(['addFonts', function() {
          // no-op hook to satisfy older typings
        }]);

        // Attach to VFS
        (jsPDF as any).API.addFileToVFS('NotoSans-Regular.ttf', rB64);
        (jsPDF as any).API.addFileToVFS('NotoSans-Bold.ttf', bB64);
        (jsPDF as any).API.addFont('NotoSans-Regular.ttf', 'NotoSans', 'normal');
        (jsPDF as any).API.addFont('NotoSans-Bold.ttf', 'NotoSans', 'bold');
        
        fontsLoaded = true;
        console.log('✅ Custom fonts registered successfully in jsPDF');
        return true;
      } catch (error) {
        console.error('❌ Font loading failed completely:', error);
        fontsLoaded = false;
        return false;
      }
    })();
  }
  return fontsReady;
};

// Enhanced formatters
const fmtDate = (d: string | Date) => new Date(d).toLocaleDateString('tr-TR');
const fmtMoney = (n: number) =>
  new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(n) + ' TL';

// Encode text for proper PDF rendering with UTF-8 support
const safePDFText = (text: string): string => {
  if (!text) return '';
  try {
    // Normalize the text for consistent Unicode representation
    const normalized = text.normalize('NFC');
    
    // If using fallback font, map Turkish characters to ASCII equivalents
    if (!fontsLoaded) {
      console.warn('Using fallback font - Turkish characters will be converted to ASCII');
      const turkishMap: Record<string, string> = {
        'ğ': 'g', 'Ğ': 'G',
        'ı': 'i', 'İ': 'I', 
        'ö': 'o', 'Ö': 'O',
        'ü': 'u', 'Ü': 'U',
        'ş': 's', 'Ş': 'S',
        'ç': 'c', 'Ç': 'C'
      };
      
      let result = normalized;
      for (const [turkish, ascii] of Object.entries(turkishMap)) {
        result = result.replace(new RegExp(turkish, 'g'), ascii);
      }
      return result;
    }
    
    // For Unicode fonts, return the properly normalized text
    return normalized;
  } catch (error) {
    console.warn('Text encoding failed:', error);
    return text;
  }
};

// Helper function to set font consistently
const setPDFFont = (pdf: jsPDF, weight: 'normal' | 'bold' = 'normal') => {
  try {
    if (fontsLoaded) {
      pdf.setFont('NotoSans', weight);
      console.log(`✅ Using NotoSans ${weight}`);
    } else {
      // Use a more Unicode-friendly fallback
      pdf.setFont('helvetica', weight);
      console.log(`⚠️ Using helvetica fallback ${weight}`);
    }
  } catch (error) {
    console.warn('Font setting failed, using default:', error);
    pdf.setFont('helvetica', weight);
  }
};

export const generatePackagePDF = async (data: PackagePDFData): Promise<Blob> => {
  try {
    // Validate input data
    if (!data) {
      throw new Error('PDF verisi bulunamadı');
    }
    if (!data.company?.name) {
      throw new Error('Şirket bilgileri eksik');
    }
    if (!data.items || data.items.length === 0) {
      throw new Error('Paket ürünleri bulunamadı');
    }

    try {
      const fontLoadResult = await ensureUnicodeFonts(); // <-- ensure font loaded
      console.log('Font loading result:', fontLoadResult);
    } catch (error) {
      console.warn('Font loading failed, proceeding with default fonts:', error);
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    
    console.log('📄 PDF document created');
    console.log('Fonts loaded status:', fontsLoaded);

    // Use appropriate font based on loading success
    setPDFFont(pdf, 'normal');

  pdf.setProperties({
    title: 'Paket Teklifi',
    subject: 'Package Proposal',
    author: 'MüşteriX',
    creator: 'MüşteriX CRM'
  });

  const page = { w: 210, h: 297, m: 18 };

  // 🎨 REFINED PALETTE (reduced orange, cool neutrals, glass look)
  const ACCENT: [number, number, number] = [255, 67, 6];      // #FF4306 (kept as accent)
  const ACCENT_SOFT: [number, number, number] = [255, 120, 80];
  const INK: [number, number, number] = [22, 22, 22];         // text main
  const INK_SOFT: [number, number, number] = [75, 85, 99];    // text secondary
  const INK_MUTED: [number, number, number] = [148, 163, 184];

  const GLASS_BG_TOP: [number, number, number] = [238, 244, 255]; // cool, airy
  const GLASS_BG_BOTTOM: [number, number, number] = [247, 248, 252];
  const GLASS_WHITE: [number, number, number] = [255, 255, 255];
  const GLASS_TINT: [number, number, number] = [248, 249, 252];
  const GLASS_BORDER: [number, number, number] = [225, 232, 240];
  const SHADOW: [number, number, number] = [200, 205, 215];

  // ===== HEADER: glassmorphic + soft gradient ribbons =====
  const headerH = 60;

  // Background vertical gradient (simulated)
  for (let i = 0; i < headerH; i++) {
    const t = i / headerH;
    const r = Math.round(GLASS_BG_TOP[0] * (1 - t) + GLASS_BG_BOTTOM[0] * t);
    const g = Math.round(GLASS_BG_TOP[1] * (1 - t) + GLASS_BG_BOTTOM[1] * t);
    const b = Math.round(GLASS_BG_TOP[2] * (1 - t) + GLASS_BG_BOTTOM[2] * t);
    pdf.setFillColor(r, g, b);
    pdf.rect(0, i, page.w, 1, 'F');
  }

  // Accent ribbons (very thin, add energy without screaming)
  pdf.setFillColor(...ACCENT);
  pdf.rect(0, 0, page.w, 2.2, 'F');
  pdf.setFillColor(...ACCENT_SOFT);
  pdf.rect(0, 2.2, page.w, 1, 'F');

  // Floating glass plate (subtle 3D)
  const plateX = page.m - 4;
  const plateY = 10;
  const plateW = page.w - (page.m - 4) * 2;
  const plateH = headerH - 18;

  // Shadow
  pdf.setFillColor(...SHADOW);
  pdf.roundedRect(plateX + 1.8, plateY + 2.2, plateW, plateH, 4, 4, 'F');

  // Glass body
  pdf.setFillColor(...GLASS_WHITE);
  pdf.roundedRect(plateX, plateY, plateW, plateH, 4, 4, 'F');
  pdf.setFillColor(...GLASS_TINT);
  pdf.roundedRect(plateX + 1, plateY + 1, plateW - 2, plateH - 2, 3.6, 3.6, 'F');

  // Top slim accent on plate
  pdf.setFillColor(...ACCENT);
  pdf.roundedRect(plateX, plateY, plateW, 3.6, 4, 4, 'F');

  // Company (left)
  pdf.setTextColor(...INK);
  setPDFFont(pdf, 'bold');
  pdf.setFontSize(18);
  pdf.text(safePDFText(data.company.name || 'Sirket Adi'), page.m, 28);

  setPDFFont(pdf, 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...INK_SOFT);
  let companyY = 36;

  if (data.company.phone) {
    pdf.text(`Tel: ${safePDFText(data.company.phone)}`, page.m, companyY);
    companyY += 6;
  }
  if (data.company.address) {
    const addressLines = pdf.splitTextToSize(safePDFText(data.company.address), 85);
    pdf.text(`Adres: ${addressLines[0]}`, page.m, companyY);
    let yOff = 6;
    for (let i = 1; i < addressLines.length; i++) {
      pdf.text(`       ${addressLines[i]}`, page.m, companyY + yOff);
      yOff += 6;
    }
  }

  // Title (right)
  const rightX = page.w - page.m;
  pdf.setTextColor(...ACCENT);
  setPDFFont(pdf, 'bold');
  pdf.setFontSize(22);
  pdf.text(safePDFText('PAKET TEKLIFI'), rightX, 26, { align: 'right' });

  setPDFFont(pdf, 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...INK_SOFT);
  pdf.text(`Tarih: ${fmtDate(data.createdAt)}`, rightX, 36, { align: 'right' });
  pdf.text(`Teklif No: ${data.id.slice(0, 8).toUpperCase()}`, rightX, 43, { align: 'right' });

  let y = headerH + 20;

  // ===== Cards: deep glass + soft 3D =====
  const cardH = 48;
  const cardGutter = 14;
  const cardW = (page.w - page.m * 2 - cardGutter) / 2;

  const drawGlassCard = (
    x: number, y0: number, w: number, h: number, title: string, icon: string, rows: Array<[string, string]>
  ) => {
    // Shadow
    pdf.setFillColor(205, 210, 220);
    pdf.roundedRect(x + 1.6, y0 + 2, w, h, 4, 4, 'F');

    // Glass layers
    pdf.setFillColor(...GLASS_WHITE);
    pdf.roundedRect(x, y0, w, h, 4, 4, 'F');
    pdf.setFillColor(...GLASS_TINT);
    pdf.roundedRect(x + 1, y0 + 1, w - 2, h - 2, 3.6, 3.6, 'F');

    // Hairline border
    pdf.setDrawColor(...GLASS_BORDER);
    pdf.setLineWidth(0.6);
    pdf.roundedRect(x, y0, w, h, 4, 4, 'D');

    // Plate accent line (thin)
    pdf.setFillColor(...ACCENT);
    pdf.roundedRect(x, y0, w, 3, 4, 4, 'F');

    // Title
    pdf.setTextColor(...INK);
    setPDFFont(pdf, 'bold');
    pdf.setFontSize(12);
    const titleText = icon ? `${icon}  ${safePDFText(title)}` : safePDFText(title);
    pdf.text(titleText, x + 10, y0 + 16);

    // Rows
    setPDFFont(pdf, 'normal');
    pdf.setFontSize(9.5);
    let ry = y0 + 25;
    rows.forEach(([label, value]) => {
      const safeLabel = safePDFText(label);
      const safeValue = safePDFText(value || '—');
      
      // Calculate label width and ensure no overlap
      const labelText = `${safeLabel}:`;
      const labelWidth = pdf.getTextWidth(labelText);
      const maxLabelWidth = w * 0.4; // 40% of card width for label
      const valueStartX = x + Math.min(labelWidth + 15, maxLabelWidth + 10);
      
      pdf.setTextColor(...INK_SOFT);
      pdf.text(labelText, x + 10, ry);
      
      pdf.setTextColor(...INK);
      setPDFFont(pdf, safeValue !== '—' ? 'bold' : 'normal');
      
      // If value is too long, wrap it
      const availableWidth = (x + w - 10) - valueStartX;
      const valueLines = pdf.splitTextToSize(safeValue, availableWidth);
      
      if (valueLines.length === 1) {
        pdf.text(safeValue, x + w - 10, ry, { align: 'right' });
      } else {
        // For multiline values, align left from value start position
        pdf.text(valueLines, valueStartX, ry);
        ry += (valueLines.length - 1) * 5; // Add extra space for additional lines
      }
      
      ry += 7;
    });
  };

  // Customer card (only show if customer exists)
  if (data.customer) {
    drawGlassCard(
      page.m, y, cardW, cardH, 'Musteri Bilgileri', '',
      [
        ['Ad Soyad', data.customer?.name || '—'],
        ['Telefon', data.customer?.phone || '—'],
        ['E-posta', data.customer?.email || '—'],
      ]
    );
  }

  // Package details card
  const packageCardX = data.customer ? page.m + cardW + cardGutter : page.m;
  drawGlassCard(
    packageCardX, y, cardW, cardH, 'Teklif Detaylari', '',
    [
      ['Paket Adi', data.title || '—'],
      ['Gecerlilik Tarihi', data.validUntil ? fmtDate(data.validUntil) : '—'],
      ['Durum', 'Aktif Teklif'],
    ]
  );

  y += cardH + 22;

  // ===== Table: refined glass grid =====
  const itemRows: RowInput[] = data.items.map((item) => ([
    safePDFText(item.name),
    safePDFText(item.code),
    String(item.quantity),
    fmtMoney(item.price),
    fmtMoney(item.total),
  ]));

  // Calculate proper table width and centering
  // Page width: 210mm, margins: 18mm each side, available: 174mm
  // Use 160mm for table to ensure proper padding from both sides
  const availableWidth = page.w - (page.m * 2); // 174mm
  const tableWidth = 160; // Leave 7mm padding on each side
  const tableMargin = (page.w - tableWidth) / 2; // Center the table

  autoTable(pdf, {
    startY: y,
    head: [['Urun Adi', 'Kod', 'Adet', 'Birim Fiyat', 'Toplam']],
    body: itemRows,
    styles: {
      font: fontsLoaded ? 'NotoSans' : 'helvetica',
      fontSize: 10,
      cellPadding: { top: 6, right: 3, bottom: 6, left: 3 },
      textColor: INK as any,
      lineColor: GLASS_BORDER as any,
      lineWidth: 0.4,
      fillColor: GLASS_WHITE as any,
      overflow: 'linebreak',
      cellWidth: 'wrap',
    },
    headStyles: {
      fillColor: [40, 44, 52] as any,
      textColor: [255, 255, 255] as any,
      halign: 'center',
      valign: 'middle',
      fontStyle: 'bold',
      fontSize: 11,
      cellPadding: { top: 8, right: 3, bottom: 8, left: 3 },
      overflow: 'linebreak',
    },
    bodyStyles: {
      fillColor: [252, 253, 255] as any,
      valign: 'middle',
    },
    alternateRowStyles: {
      fillColor: [245, 248, 252] as any,
    },
    columnStyles: {
      0: { cellWidth: 70, halign: 'left', overflow: 'linebreak' }, // Product name
      1: { cellWidth: 30, halign: 'center' }, // Code
      2: { cellWidth: 20, halign: 'center' }, // Quantity
      3: { cellWidth: 20, halign: 'right' }, // Unit price
      4: { cellWidth: 20, halign: 'right', fontStyle: 'bold' }, // Total
    },
    theme: 'grid',
    margin: { left: tableMargin, right: tableMargin },
    tableWidth: tableWidth,
    showHead: 'everyPage',
  });

  y = (pdf as any).lastAutoTable.finalY + 28; // Increased spacing from table (was 18)

  // ===== Totals: elevated glass block =====
  const totalsW = 100;
  const totalsX = page.w - page.m - totalsW;
  const totalsH = data.discount > 0 ? 75 : 65; // Increased height for better spacing (was 68/58)

  // Shadow
  pdf.setFillColor(205, 210, 220);
  pdf.roundedRect(totalsX + 1.8, y + 2, totalsW, totalsH, 4, 4, 'F');

  // Body
  pdf.setFillColor(...GLASS_WHITE);
  pdf.roundedRect(totalsX, y, totalsW, totalsH, 4, 4, 'F');
  pdf.setFillColor(...GLASS_TINT);
  pdf.roundedRect(totalsX + 1, y + 1, totalsW - 2, totalsH - 2, 3.6, 3.6, 'F');

  // Border + top accent
  pdf.setDrawColor(...GLASS_BORDER);
  pdf.setLineWidth(0.8);
  pdf.roundedRect(totalsX, y, totalsW, totalsH, 4, 4, 'D');
  pdf.setFillColor(...ACCENT);
  pdf.roundedRect(totalsX, y, totalsW, 3, 4, 4, 'F');

  const labelX = totalsX + 14; // Increased left padding (was 10)
  let ty = y + 18; // Increased top padding (was 14)

  const drawTotalRow = (label: string, value: string, isTotal = false, isDiscount = false) => {
    setPDFFont(pdf, isTotal ? 'bold' : 'normal');
    pdf.setFontSize(isTotal ? 13 : 11);
    pdf.setTextColor(...(isTotal ? ACCENT : INK_SOFT));
    pdf.text(safePDFText(label), labelX, ty);
    pdf.setTextColor(...(isDiscount ? ACCENT : isTotal ? ACCENT : INK));
    pdf.text(value, totalsX + totalsW - 14, ty, { align: 'right' }); // Increased right padding (was 10)
    ty += isTotal ? 14 : 8.5; // Increased spacing between rows (was 12/7.2)
  };

  drawTotalRow('Ara Toplam', fmtMoney(data.subtotal));
  if (data.discount > 0) {
    drawTotalRow('Indirim', `- ${fmtMoney(data.discount)}`, false, true);
  }

  // Add spacing before separator
  ty += 6; // Increased spacing before separator (was 4)

  // Separator
  pdf.setDrawColor(...ACCENT);
  pdf.setLineWidth(1.2);
  pdf.line(totalsX + 14, ty - 2, totalsX + totalsW - 14, ty - 2); // Increased padding from edges (was 10)

  // Add spacing after separator
  ty += 8; // Increased spacing after separator (was 6)

  drawTotalRow('TOPLAM', fmtMoney(data.total), true);

  // ===== Notes (if any): soft card =====
  if (data.note) {
    let notesY = y + totalsH + 18;
    if (notesY > page.h - 70) {
      pdf.addPage();
      notesY = page.m;
    }

    setPDFFont(pdf, 'bold');
    pdf.setFontSize(13.5);
    pdf.setTextColor(...INK);
    pdf.text(safePDFText('Notlar'), page.m, notesY);

    const notesPadding = 11;
    const notesContentY = notesY + 9;
    const noteLines = pdf.splitTextToSize(safePDFText(data.note), page.w - page.m * 2 - notesPadding * 2);
    const notesBoxH = noteLines.length * 6 + notesPadding * 2;

    // Shadow + glass
    pdf.setFillColor(210, 214, 222);
    pdf.roundedRect(page.m + 1.4, notesContentY + 1.8, page.w - page.m * 2, notesBoxH, 4, 4, 'F');

    pdf.setFillColor(...GLASS_TINT);
    pdf.roundedRect(page.m, notesContentY, page.w - page.m * 2, notesBoxH, 4, 4, 'F');

    pdf.setDrawColor(...GLASS_BORDER);
    pdf.setLineWidth(0.6);
    pdf.roundedRect(page.m, notesContentY, page.w - page.m * 2, notesBoxH, 4, 4, 'D');

    setPDFFont(pdf, 'normal');
    pdf.setFontSize(10.2);
    pdf.setTextColor(...INK_SOFT);
    pdf.text(noteLines, page.m + notesPadding, notesContentY + notesPadding + 5.2);
  }

  // ===== Footer =====
  const footerY = pdf.internal.pageSize.getHeight() - 18;

  // Soft gradient footer background (simulated)
  for (let i = 0; i < 22; i++) {
    const t = i / 22;
    const r = Math.round(248 * (1 - t) + 255 * t);
    const g = Math.round(249 * (1 - t) + 247 * t);
    const b = Math.round(252 * (1 - t) + 245 * t);
    pdf.setFillColor(r, g, b);
    pdf.rect(0, footerY - 6 + i, page.w, 1, 'F');
  }

  // Accent lines
  pdf.setFillColor(...ACCENT);
  pdf.rect(0, footerY - 7, page.w, 2, 'F');
  pdf.setFillColor(...ACCENT_SOFT);
  pdf.rect(0, footerY - 5, page.w, 0.8, 'F');

  // Branding + page number
  setPDFFont(pdf, 'bold');
  pdf.setFontSize(11.5);
  pdf.setTextColor(...ACCENT);
  pdf.text(safePDFText('www.musterix.com ile uretilmistir'), page.w / 2, footerY + 3, { align: 'center' });

  setPDFFont(pdf, 'normal');
  pdf.setFontSize(9.2);
  pdf.setTextColor(...INK_MUTED);
  const pageNum = (pdf as any).getCurrentPageInfo().pageNumber;
  pdf.text(safePDFText(`Sayfa ${pageNum}`), page.w - page.m, footerY + 3, { align: 'right' });

    return pdf.output('blob');
  } catch (error) {
    console.error('PDF generation failed:', error);
    if (error instanceof Error) {
      throw new Error(`PDF oluşturulurken hata oluştu: ${error.message}`);
    } else {
      throw new Error('PDF oluşturulurken bilinmeyen bir hata oluştu');
    }
  }
};

// ======================= storage helpers (değişmedi) =======================
export const uploadPackagePDF = async (
  pdfBlob: Blob, 
  packageId: string, 
  companyId: string,
  packageTitle: string
): Promise<string> => {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  const fileName = `${companyId}/package-${packageId}-${timestamp}-${randomSuffix}.pdf`;

  try {
    const { data: existingFiles } = await supabase.storage
      .from('package-pdfs')
      .list(companyId, { search: `package-${packageId}-` });
    if (existingFiles && existingFiles.length > 0) {
      const oldFiles = existingFiles.map(file => `${companyId}/${file.name}`);
      await supabase.storage.from('package-pdfs').remove(oldFiles);
    }
  } catch (cleanupError) {
    console.warn('Could not clean up old PDFs:', cleanupError);
  }

  const { data, error } = await supabase.storage
    .from('package-pdfs')
    .upload(fileName, pdfBlob, { contentType: 'application/pdf', upsert: false, cacheControl: 'no-cache' });

  if (error) {
    console.error('Error uploading PDF:', error);
    throw new Error('PDF yüklenirken hata oluştu');
  }
  console.log(`PDF uploaded successfully: ${data.path}`);
  return data.path;
};

export const getPackagePDFUrl = async (filePath: string): Promise<string> => {
  console.log('🔗 Generating public URL for PDF path:', filePath);
  
  // Verify file exists in storage first
  try {
    // Extract folder and filename from path (format: companyId/filename)
    const pathParts = filePath.split('/');
    const folder = pathParts[0];
    const fileName = pathParts.slice(1).join('/');
    
    console.log('📁 Checking file existence:', { folder, fileName, fullPath: filePath });
    
    // List files in the folder to verify existence
    const { data: files, error: listError } = await supabase.storage
      .from('package-pdfs')
      .list(folder, {
        search: fileName.split('-').slice(0, 2).join('-') // Search by package ID prefix
      });
    
    if (listError) {
      console.warn('⚠️ Could not list files (continuing anyway):', listError);
    } else {
      console.log('📋 Files found in folder:', files?.map(f => f.name) || []);
      const fileExists = files?.some(f => f.name === fileName || filePath.includes(f.name));
      if (!fileExists && files && files.length > 0) {
        console.warn('⚠️ Exact file not found, but similar files exist:', files.map(f => f.name));
      }
    }
  } catch (checkError) {
    console.warn('⚠️ File existence check failed (continuing anyway):', checkError);
  }
  
  // Storage is public, so we can use the public URL directly
  const { data: publicData, error } = supabase.storage.from('package-pdfs').getPublicUrl(filePath);
  
  if (error) {
    console.error('❌ Error generating public URL:', error);
    throw new Error(`PDF URL oluşturulamadı: ${error.message}`);
  }
  
  if (!publicData?.publicUrl) {
    console.error('❌ No public URL returned for path:', filePath);
    throw new Error('PDF URL oluşturulamadı: Geçersiz dosya yolu');
  }
  
  console.log('✅ Generated public URL:', publicData.publicUrl);
  return publicData.publicUrl;
};

export const sharePackagePDF = async (filePath: string): Promise<void> => {
  try {
    const url = await getPackagePDFUrl(filePath);
    if (navigator.share) {
      await navigator.share({ title: 'Paket Teklifi', text: 'Paket teklifi PDF dosyası', url });
    } else {
      await navigator.clipboard.writeText(url);
      console.log('PDF URL copied to clipboard');
    }
  } catch (error) {
    console.error('Error sharing PDF:', error);
    throw error;
  }
};

export const fetchCompanyForPDF = async (companyId: string): Promise<{ name: string; phone?: string; address?: string }> => {
  const { data, error } = await supabase
    .from('companies')
    .select('name, phone, address')
    .eq('id', companyId)
    .single();

  if (error || !data) {
    console.error('Error fetching company for PDF:', error);
    throw new Error('Şirket bilgileri alınamadı');
  }

  const companyData = data as any;
  return {
    name: companyData.name || 'Şirket Adı',
    phone: companyData.phone || undefined,
    address: companyData.address || undefined,
  };
};
